import { Loader2 } from "lucide-react";
import { useState, useCallback, type FormEvent } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/AuthContext";
import { deleteMyAccount } from "@/services/authService";

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountEmail: string;
}

export function DeleteAccountDialog({
  open,
  onOpenChange,
  accountEmail,
}: DeleteAccountDialogProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearLocalSession, user } = useAuth();
  const hasPassword = user?.hasPassword !== false;
  const [confirmEmail, setConfirmEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenChange = (next: boolean): void => {
    if (!next && isDeleting) return;
    onOpenChange(next);
    if (!next) {
      setConfirmEmail("");
      setCurrentPassword("");
    }
  };

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      setIsDeleting(true);
      try {
        await deleteMyAccount({
          confirmEmail,
          currentPassword: currentPassword.trim() || undefined,
        });
        clearLocalSession();
        toast.success(t("profile.deleteAccount.success"));
        navigate("/login", { replace: true });
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number; data?: { message?: string } } })
          ?.response?.status;
        const message = (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message;
        if (status === 401) {
          toast.error(t("profile.deleteAccount.wrongPassword"));
        } else if (status === 403) {
          toast.error(t("profile.deleteAccount.forbidden"));
        } else if (status === 400 && typeof message === "string") {
          if (message.includes("Email confirmation")) {
            toast.error(t("profile.deleteAccount.emailMismatch"));
          } else if (message.includes("Current password")) {
            toast.error(t("profile.deleteAccount.currentPasswordRequired"));
          } else {
            toast.error(message);
          }
        } else {
          toast.error(t("profile.deleteAccount.error"));
        }
      } finally {
        setIsDeleting(false);
      }
    },
    [confirmEmail, currentPassword, clearLocalSession, navigate, t]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(ev) => {
        if (isDeleting) ev.preventDefault();
      }}>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {t("profile.deleteAccount.title")}
            </DialogTitle>
            <DialogDescription className="text-left">
              {t("profile.deleteAccount.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="delete-account-email">{t("profile.deleteAccount.confirmEmailLabel")}</Label>
              <Input
                id="delete-account-email"
                type="email"
                autoComplete="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                disabled={isDeleting}
                placeholder={accountEmail}
                className="transition-colors duration-150"
              />
            </div>
            {hasPassword ? (
              <div className="grid gap-2">
                <Label htmlFor="delete-account-password">{t("profile.deleteAccount.currentPasswordLabel")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("profile.deleteAccount.currentPasswordHint")}
                </p>
                <PasswordInput
                  id="delete-account-password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isDeleting}
                  className="transition-colors duration-150"
                />
              </div>
            ) : null}
          </div>
          <DialogFooter className="flex flex-row justify-between gap-2 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isDeleting}
              className="transition-colors duration-150"
            >
              {t("profile.deleteAccount.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={isDeleting || !confirmEmail.trim()}
              className="transition-colors duration-150"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("profile.deleteAccount.deleting")}
                </>
              ) : (
                t("profile.deleteAccount.confirm")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
